/**
 * F-A FR-A5 of task `2026-05-28_observability-non-export-operations`.
 *
 * Fire-and-forget audit POST to `/api/validation-runs` after every
 * `ExecuteValidation` gRPC call. Mirrors the export-side
 * `apiClient.post('/pcf-runs', ...)` round-trip — keeps the
 * traceparent propagation (apiClient interceptors already attach it)
 * so the API row's TraceId stitches to the Tempo span tree the React
 * client owns.
 *
 * Idempotent on the API side (unique index on (TraceId, Operation));
 * a retry from the orchestrator is a 200 OK update-or-noop, never a
 * duplicate row.
 */
import apiClient from './apiClient';

/** Generate a W3C trace_id (16 random bytes → 32 hex chars). Mirrors
 * the helper in `pcfExportClient.ts` — kept local so the validation
 * audit flow doesn't depend on the export client's internals. */
export function generateValidationTraceId(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) {
    out += bytes[i].toString(16).padStart(2, '0');
  }
  return out;
}

/** Shape we POST to the API. Field names match
 * `ValidationRunRequest` server-side, web-default JSON options
 * (camelCase keys). */
export interface ValidationRunPayload {
  traceId: string;
  operation: string;
  statusText: string;
  startedAtUtc?: string;
  finishedAtUtc?: string;
  durationMs: number;
  elementCount: number;
  ruleCount: number;
  errorCount: number;
  warningCount: number;
  modelName?: string;
  errorStage?: string;
  errorMessage?: string;
}

/** Outcome of the audit POST. `success=false` does NOT block the
 * caller — validation already completed; the audit row is best-effort
 * and a failure is logged but otherwise ignored (the trace_id still
 * lives in Loki via the F5 enrichment, so support can still find the
 * run). */
export interface PostValidationRunResult {
  success: boolean;
  id?: string;
  status?: string;
  error?: string;
}

/**
 * POST one validation-run audit row. Caller passes a fully-populated
 * payload; we just forward via apiClient (axios already attaches the
 * JWT + traceparent interceptors). On any error we log + swallow —
 * the audit write is best-effort and must never block the UI.
 */
export async function postValidationRun(
  payload: ValidationRunPayload
): Promise<PostValidationRunResult> {
  try {
    const resp = await apiClient.post('/validation-runs', payload);
    const data = (resp as { data?: { id?: string; status?: string } })?.data ?? {};
    return {
      success: true,
      id: data.id,
      status: data.status,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'POST /validation-runs failed';
    return { success: false, error: message };
  }
}
